var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useContext, useState, useEffect } from 'react';
import { Typography, Button } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService, } from '@thd-nucleus/experience-context';
import { useNavigate } from '@thd-olt-component-react/router';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { getPaymentsErrorCode, getPaymentsErrorMessage, } from '../../../utils/payments';
import { b2bPaymentsApi } from '../../../api/b2b/payments';
import { usePayments } from '../../../hooks/use-payments';
import { useModal } from '../../../hooks/use-modal';
import { useGetRecaptchaToken } from '../../../hooks/use-get-recaptcha-token';
import { InfoPopover } from '../../shared/popover/popover';
import { CardType } from '../../../constants/payments';
import { PROXTRA_DOLLARS_MESSAGE, GIFT_CARDS_MESSAGE, } from '../../../constants/popover-content';
import { AddCardModal } from '../add-tender/add-tender-modal';
import { ADD_GIFT_CARD, ADD_CHECKING_ACCOUNT, ADD_KEY_TAG, } from '../../../constants/response-success-messages';
import { ERR_UNABLE_TO_SAVE, ERR_GENERIC, } from '../../../constants/response-error-messages';
import { RecaptchaAction } from '../../../constants/recaptcha';
import { setOverlayEvent, setPageInfo } from '../../../utils/analytics';
import { setAddCardAnalytics, setOtherButtonSubmissionAnalytics, } from '../../../utils/payments-analytics';
import { getSiteKey, getPaymentModalsVersion, getAddGiftCardSiteKey, } from '../../../utils/general';
import { newrelic, NEW_RELIC_CHECKING_PREFIX, createNewRelicActions, NEW_RELIC_RSA_PREFIX, } from '../../../utils/newrelic';
import { B2B_PURCHASE_GIFT_CARD_PATH } from '../../../constants/links';
import { useSearchParam } from '../../../hooks/use-search-param';
import { rsaEncryption } from '../../../utils/rsa-encryption';
import { paymentsApi } from '../../../api/common/payments';
import { PUBLIC_KEY_RETRIEVAL_FAILED } from '../../../constants/error-codes';
const cardTypesTitle = {
    [CardType.CREDIT_CARD]: 'Credit Cards',
    [CardType.PRO_XTRA_DOLLARS]: 'Pro Xtra Dollars',
    [CardType.GIFT_CARD]: 'Gift Cards',
    [CardType.PAYPAL_ACCOUNT]: 'PayPal',
    [CardType.CHECKING_ACCOUNT]: 'Checking Accounts',
    [CardType.KEY_TAG]: 'Keytags',
    [CardType.PRO_HOUSE_ACCOUNT]: 'Lines Of Credit',
};
const cardTypesAddBtnLabel = {
    [CardType.CREDIT_CARD]: 'Credit Card',
    [CardType.GIFT_CARD]: 'Gift Card',
    [CardType.CHECKING_ACCOUNT]: 'Checking Account',
    [CardType.KEY_TAG]: 'Keytag',
};
const PaymentsHeaderContent = ({ cardType, addBtn, isBulkAddAvailable = false, onActionFailure, onActionSuccess, }) => {
    const { channel } = useContext(ExperienceContext);
    const { paginationInfo } = usePayments();
    const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
    const { openModal, setComponentProps, hideModal } = useModal();
    const navigate = useNavigate();
    const cardLength = {
        [CardType.CREDIT_CARD]: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCreditCards) || 0,
        [CardType.PRO_XTRA_DOLLARS]: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalProXtraDollars) || 0,
        [CardType.GIFT_CARD]: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalGiftCards) || 0,
        [CardType.PAYPAL_ACCOUNT]: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalPayPalAccounts) || 0,
        [CardType.CHECKING_ACCOUNT]: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCheckingAccounts) || 0,
        [CardType.KEY_TAG]: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalKeyTags) || 0,
        [CardType.PRO_HOUSE_ACCOUNT]: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalLinesOfCredit) || 0,
    };
    const checkingAction = createNewRelicActions(NEW_RELIC_CHECKING_PREFIX);
    const rsaAction = createNewRelicActions(NEW_RELIC_RSA_PREFIX);
    const isMobile = channel === 'mobile';
    const paymentModalsVersion = getPaymentModalsVersion();
    const fromProAllowance = useSearchParam('fromProAllowance') === 'true';
    const isBulkCardAddMicroUiEnabled = !!useConfigService('BULK_CARD_ADD_MICRO_UI_ENABLED');
    useEffect(() => {
        // bulk add operations only
        const iFrameListener = (event) => {
            var _a, _b, _c, _d, _e, _f;
            // iframe loaded
            if ((_a = event.data) === null || _a === void 0 ? void 0 : _a.loaded) {
                window.postMessage({
                    event: 'experience data',
                    siteKey: getSiteKey(true),
                    experienceProps: window === null || window === void 0 ? void 0 : window.__EXPERIENCE_PROPS__,
                    experienceContext: window === null || window === void 0 ? void 0 : window.__EXPERIENCE_CONTEXT__,
                }, '*');
            }
            if ((_b = event.data) === null || _b === void 0 ? void 0 : _b.iframeClosed) {
                setIsBulkAddOpen(false);
            }
            if (((_d = (_c = event.data) === null || _c === void 0 ? void 0 : _c.bulkResponse) === null || _d === void 0 ? void 0 : _d.numberOfCardsSuccess) > 0) {
                if (onActionSuccess) {
                    onActionSuccess('', `Success! All ${(_e = event.data) === null || _e === void 0 ? void 0 : _e.bulkResponse.numberOfCardsSuccess} cards have been added to your account.`);
                }
                setOtherButtonSubmissionAnalytics(`cards and accounts: bulk add success: ${(_f = event.data) === null || _f === void 0 ? void 0 : _f.bulkResponse.numberOfCardsSuccess}}`);
            }
        };
        // send info to iframe once it's loaded
        window.addEventListener('message', iFrameListener, false);
        return () => {
            window.removeEventListener('message', iFrameListener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const isAddGiftCardRecaptchaEnabled = !!useConfigService('IS_ADD_GIFTCARD_RECAPTCHA_ENABLED');
    const isCheckingAuthRsaEncryptionEnabled = !!useConfigService('CHECKING_AUTH_RSA_ENCRYPTION_ENABLED');
    const recaptchaEnabled = cardType === CardType.GIFT_CARD && isAddGiftCardRecaptchaEnabled;
    const { getRecaptchaToken } = useGetRecaptchaToken(recaptchaEnabled);
    const getCheckingPublicKey = () => {
        const action = rsaAction.GET_RSA_PUBLIC_KEY;
        return paymentsApi
            .getRsaPublicKey()
            .then((PUBLIC_KEY) => {
            if (!PUBLIC_KEY) {
                newrelic.addPageAction(action, {
                    result: 'error',
                });
                return null;
            }
            newrelic.addPageAction(action, {
                result: 'success',
            });
            return PUBLIC_KEY;
        })
            .catch(() => {
            newrelic.addPageAction(action, {
                result: 'error',
            });
            return null;
        });
    };
    const onAddPayment = (cardNickName, cardNumber, routingNumber) => __awaiter(void 0, void 0, void 0, function* () {
        let newPayment;
        let successMessage;
        let analyticsCategory;
        let recaptchaToken;
        switch (cardType) {
            case CardType.GIFT_CARD:
                newPayment = {
                    cardNickName: cardNickName || '',
                    cardNumber,
                    isActionSetAsDefault: false,
                };
                successMessage = ADD_GIFT_CARD.message;
                analyticsCategory = 'gift card';
                if (isAddGiftCardRecaptchaEnabled) {
                    recaptchaToken = yield getRecaptchaToken(RecaptchaAction.ADD_GIFT_CARD);
                }
                break;
            case CardType.CHECKING_ACCOUNT:
                newPayment = {
                    cardNickName: cardNickName || '',
                    cardNumber,
                    routingNumber,
                    isActionSetAsDefault: false,
                };
                successMessage = ADD_CHECKING_ACCOUNT.message;
                analyticsCategory = 'checking account';
                break;
            case CardType.KEY_TAG:
                newPayment = {
                    cardNickName,
                    cardNumber,
                    cardStatus: 'Active',
                    isActionSetAsDefault: false,
                };
                if (cardNickName !== '' || cardNickName !== undefined)
                    newPayment.cardNickName = cardNickName;
                successMessage = ADD_KEY_TAG.message;
                analyticsCategory = 'keytag';
                break;
            default:
                throw Error('Necessary payment info not provided');
        }
        const b2bPaymentsApiCall = () => {
            if (isCheckingAuthRsaEncryptionEnabled &&
                cardType === CardType.CHECKING_ACCOUNT) {
                return getCheckingPublicKey()
                    .then((PUBLIC_KEY) => {
                    if (!PUBLIC_KEY) {
                        throw new Error(PUBLIC_KEY_RETRIEVAL_FAILED);
                    }
                    return rsaEncryption(cardNumber, PUBLIC_KEY);
                })
                    .then((encryptedAccountNumber) => {
                    // New v2 addCheckingAccount endpoint payload
                    const newPaymentEncrypted = {
                        encryptedAccountNumber,
                        routingNumber,
                        cardNickName: cardNickName || '',
                    };
                    return b2bPaymentsApi.addCheckingAccountWithEncryption(newPaymentEncrypted);
                });
            }
            return b2bPaymentsApi.addPayment(newPayment, cardType, recaptchaToken);
        };
        const action = isCheckingAuthRsaEncryptionEnabled
            ? rsaAction.SUBMIT
            : checkingAction.SUBMIT;
        setComponentProps({ isLoading: true });
        b2bPaymentsApiCall()
            .then(() => {
            onActionSuccess('', successMessage);
            // cardType is CardType.GIFT_CARD | CardType.CHECKING_ACCOUNT | CardType.KEY_TAG so default and brand do no exist
            setAddCardAnalytics(analyticsCategory, false, '', '');
            if (cardType === CardType.CHECKING_ACCOUNT) {
                newrelic.addPageAction(action, {
                    result: 'success',
                });
            }
        })
            .catch((error) => {
            const errCode = getPaymentsErrorCode(error);
            let errorMessage = ERR_UNABLE_TO_SAVE.message;
            // Handle RSA public key retrieval failure
            if ((error === null || error === void 0 ? void 0 : error.message) === PUBLIC_KEY_RETRIEVAL_FAILED) {
                errorMessage = ERR_GENERIC.message;
            }
            if (cardType === CardType.CHECKING_ACCOUNT) {
                newrelic.addPageAction(action, {
                    errorCode: errCode,
                    errMessage: getPaymentsErrorMessage(error),
                    result: 'error',
                });
            }
            setPageInfo('error', 'inline', `add ${analyticsCategory}`, errorMessage, errCode || 'n/a');
            onActionFailure('', errorMessage);
        })
            .finally(() => {
            setComponentProps({ isLoading: false });
            hideModal();
        });
    });
    const onAdd = () => {
        // handle add click for specific type
        if (cardType === CardType.GIFT_CARD) {
            const props = {
                cardType: CardType.GIFT_CARD,
                isLoading: false,
                onAddCard: onAddPayment,
            };
            setOverlayEvent('add gift card');
            openModal(AddCardModal, props);
        }
        if (cardType === CardType.CHECKING_ACCOUNT) {
            const props = {
                cardType: CardType.CHECKING_ACCOUNT,
                isLoading: false,
                onAddCard: onAddPayment,
            };
            setOverlayEvent('add checking account');
            openModal(AddCardModal, props);
        }
        if (cardType === CardType.KEY_TAG) {
            const props = {
                cardType: CardType.KEY_TAG,
                isLoading: false,
                onAddCard: onAddPayment,
            };
            setOverlayEvent('add keytag');
            return openModal(AddCardModal, props);
        }
        if (cardType === CardType.CREDIT_CARD) {
            setOverlayEvent('add credit card');
            navigate(fromProAllowance
                ? '/myaccount/payments/addcreditcard?fromProAllowance=true'
                : '/myaccount/payments/addcreditcard');
        }
        return '';
    };
    const getPopoverContent = (type) => {
        switch (type) {
            case CardType.PRO_XTRA_DOLLARS:
                return {
                    title: PROXTRA_DOLLARS_MESSAGE.title,
                    message: PROXTRA_DOLLARS_MESSAGE.message,
                };
            case CardType.GIFT_CARD:
                return {
                    title: GIFT_CARDS_MESSAGE.title,
                    message: GIFT_CARDS_MESSAGE.message,
                };
            default:
                return '';
        }
    };
    const popoverContent = getPopoverContent(cardType);
    const iconSize = isMobile ? 'small' : 'regular';
    const isGiftCard = cardType === CardType.GIFT_CARD;
    const renderAddButton = () => (React.createElement(Button, { fullWidth: true, onClick: onAdd, "data-testid": `${cardType}-add-btn` },
        "Add ",
        cardTypesAddBtnLabel[cardType]));
    const iframeSrc = `/account/payment-ui/${paymentModalsVersion}/dist/client/bulk-add.html`;
    // local dev url
    // const iframeSrc = '/modals/bulk-add.html'
    return (React.createElement("div", { className: "sui-grid sui-grid-cols-1 md:sui-grid-cols-6 sui-gap-y-6 md:sui-gap-x-8 md:sui-gap-y-6 sui-mb-6 md:sui-mb-6 sui-items-start sui-items-top" },
        React.createElement("div", { className: "sui-flex sui-flex-col sui-order-3 md:sui-order-1 sui-col-span-1 md:sui-col-span-3" }, isGiftCard ? (React.createElement(React.Fragment, null,
            React.createElement(Typography, { weight: "display", variant: "h2", uppercase: true },
                cardTypesTitle[cardType],
                " (",
                cardLength[cardType],
                ")"),
            React.createElement(Typography, { variant: "body-base" },
                React.createElement("div", { className: "sui-pt-4" }, "Saving a gift card allows you to track purchases made online and in-store with this card.")))) : (React.createElement(Typography, { weight: "display", variant: "h2", uppercase: true },
            cardTypesTitle[cardType],
            " (",
            cardLength[cardType],
            ")",
            popoverContent && (React.createElement(InfoPopover, { iconSize: iconSize, title: popoverContent.title },
                React.createElement(Typography, { variant: "body-base" }, popoverContent.message)))))),
        isBulkAddAvailable && paymentModalsVersion && (React.createElement("div", { className: "sui-flex sui-order-2 md:sui-order-2 sui-col-span-1 sui-justify-end" },
            React.createElement("button", { type: "button", onClick: () => {
                    if (isBulkCardAddMicroUiEnabled) {
                        navigate('/myaccount/payments/bulkaddcreditcards');
                        return;
                    }
                    if (!isBulkAddOpen) {
                        setIsBulkAddOpen(true);
                        setOverlayEvent('bulk add credit cards');
                    }
                }, "data-testid": "bulk-add-cards-btn" },
                React.createElement(Typography, { decoration: "underline" }, "Bulk Add Cards")),
            isBulkAddOpen && (React.createElement("iframe", { title: "Bulk Add Card Modal", src: iframeSrc, className: "sui-fixed sui-z-[901] sui-top-0 sui-bottom-0 sui-left-0 sui-right-0 sui-w-screen sui-h-screen", "data-testid": "bulk-add-cards-modal" })))),
        addBtn ? (React.createElement("div", { className: "sui-flex sui-order-1 md:sui-order-3 sui-col-span-1 md:sui-col-span-2 md:sui-col-start-5" }, addBtn)) : (cardType in cardTypesAddBtnLabel &&
            (isGiftCard ? (React.createElement("div", { className: "sui-col-span-3 md:sui-col-start-4 md:sui-order-3 lg:sui-col-start-5 lg:sui-col-span-2 2xl:sui-col-start-4 2xl:sui-col-span-3" },
                React.createElement("div", { className: "sui-grid sui-grid-rows-2 sui-grid-flow-col sui-gap-y-4 2xl:sui-grid-rows-1 2xl:sui-grid-cols-3 2xl:sui-gap-x-8 2xl:sui-grid-flow-row" },
                    React.createElement("div", { className: "sui-order-2 2xl:sui-col-start-1 2xl:sui-col-span-1 2xl:-sui-ml-[32px]" },
                        React.createElement("a", { href: B2B_PURCHASE_GIFT_CARD_PATH },
                            React.createElement(Button, { fullWidth: true, style: { minWidth: '177px' }, variant: "secondary" }, "Purchase Gift Card"))),
                    React.createElement("div", { className: "sui-order-1 2xl:sui-order-3 2xl:sui-col-start-2 2xl:sui-col-span-2" }, renderAddButton())))) : (React.createElement("div", { className: "sui-flex sui-order-1 md:sui-order-3 sui-col-span-1 md:sui-col-span-2 md:sui-col-start-5" }, renderAddButton()))))));
};
export const PaymentsHeader = ({ cardType, addBtn, isBulkAddAvailable = false, onActionFailure, onActionSuccess, }) => {
    const isAddGiftCardRecaptchaEnabled = !!useConfigService('IS_ADD_GIFTCARD_RECAPTCHA_ENABLED');
    if (cardType === CardType.GIFT_CARD && isAddGiftCardRecaptchaEnabled) {
        return (React.createElement(GoogleReCaptchaProvider, { useEnterprise: true, reCaptchaKey: getAddGiftCardSiteKey() },
            React.createElement(PaymentsHeaderContent, { cardType: cardType, addBtn: addBtn, isBulkAddAvailable: isBulkAddAvailable, onActionFailure: onActionFailure, onActionSuccess: onActionSuccess })));
    }
    return (React.createElement(PaymentsHeaderContent, { cardType: cardType, addBtn: addBtn, isBulkAddAvailable: isBulkAddAvailable, onActionFailure: onActionFailure, onActionSuccess: onActionSuccess }));
};
